import { getFileKey } from "@/lib/file-key";
import {
  canStoreDraftFilesInOpfs,
  readDraftFileFromOpfs,
  removeDraftFileFromOpfs,
  removeDraftFilesFromOpfsExcept,
  writeDraftFileToOpfs,
} from "@/lib/storage/draft-opfs-store";
import {
  FILE_STORE_NAME,
  openSurfClipsAIDb,
  THUMBNAIL_STORE_NAME,
} from "@/lib/storage/indexed-db";
import {
  listIncompleteUploadManifests,
  loadUploadManifest,
} from "@/lib/storage/upload-manifest-store";

/**
 * File keys still required by an in-flight upload so it can resume after a
 * refresh. Several sessions can upload at once, so any cleanup must preserve
 * every claimed key, not just the set being saved by the current caller.
 */
async function collectClaimedFileKeys(
  exceptClientSessionId?: string,
): Promise<Set<string>> {
  const manifests = await listIncompleteUploadManifests();
  const claimed = new Set<string>();
  for (const manifest of manifests) {
    if (manifest.clientSessionId === exceptClientSessionId) continue;
    for (const file of manifest.files) {
      claimed.add(file.clientFileKey);
    }
  }
  return claimed;
}

export type DraftFileDescriptor = {
  key: string;
  name: string;
  type: string;
  lastModified: number;
};

type DraftFileRecord = DraftFileDescriptor & {
  storage?: "opfs" | "idb";
  blob?: Blob;
};

type ThumbnailRecord = {
  key: string;
  blob: Blob;
};

type SaveDraftFilesOptions = {
  allowEmpty?: boolean;
  /**
   * Reports how many bytes of each file have been persisted locally, so the UI
   * can show a per-clip "saving locally" progress bar. Files already on disk are
   * reported as fully saved immediately.
   */
  onFileProgress?: (key: string, bytesSaved: number, totalBytes: number) => void;
};

class DraftStorageUnavailableError extends Error {
  constructor() {
    super("Clip storage is unavailable in this browser.");
    this.name = "DraftStorageUnavailableError";
  }
}

export class DraftStorageCancelledError extends Error {
  constructor() {
    super("Draft storage operation cancelled");
    this.name = "DraftStorageCancelledError";
  }
}

/**
 * Maps a draft-save failure to user-facing copy. Source files that became
 * unreadable after selection (moved, deleted, or changed on disk, often from
 * cloud sync evicting the local copy) surface as NotReadableError/NotFoundError;
 * give those a clear re-add path instead of a raw stream error.
 */
export function describeDraftStorageError(error: unknown) {
  if (error instanceof Error && error.name === "DraftStorageUnavailableError") {
    return "This browser cannot store clips locally for refresh. Keep this tab open until you group and upload.";
  }

  if (error instanceof DOMException && error.name === "QuotaExceededError") {
    return "Your browser storage is full. Remove some clips or free disk space to keep your selection after refresh.";
  }

  if (
    error instanceof DOMException &&
    (error.name === "NotReadableError" || error.name === "NotFoundError")
  ) {
    return "Some clips could not be read from your device. They may have been moved, deleted, or changed since you picked them. Please re-add them and try again.";
  }

  return "Could not save your selected clips for refresh. Try removing a few files or freeing disk space.";
}

let draftStorageGeneration = 0;

export function bumpDraftStorageGeneration() {
  draftStorageGeneration += 1;
  invalidateDraftFilesCache();
}

function assertDraftStorageActive(generation: number) {
  if (draftStorageGeneration !== generation) {
    throw new DraftStorageCancelledError();
  }
}

const DRAFT_LOG_PREFIX = "[surf-clips-ai draft]";

function logDraft(message: string, ...details: unknown[]) {
  if (!import.meta.env.DEV) return;
  if (details.length === 0) {
    console.info(DRAFT_LOG_PREFIX, message);
    return;
  }
  console.info(DRAFT_LOG_PREFIX, message, ...details);
}

function formatDraftByteSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function describeDraftFiles(files: File[]) {
  const bytes = files.reduce((sum, file) => sum + file.size, 0);
  const label = files.length === 1 ? "clip" : "clips";
  return `${files.length} ${label} (${formatDraftByteSize(bytes)})`;
}

async function recordToFile(record: DraftFileRecord) {
  if (record.blob) {
    return new File([record.blob], record.name, {
      type: record.type,
      lastModified: record.lastModified,
    });
  }

  if (!canStoreDraftFilesInOpfs()) return null;

  try {
    const blob = await readDraftFileFromOpfs(record.key);
    return new File([blob], record.name, {
      type: record.type,
      lastModified: record.lastModified,
    });
  } catch (error) {
    console.error("Failed to restore draft file from disk", record.key, error);
    return null;
  }
}

let cachedDraftFiles: File[] | null = null;
let cachedDraftFilesPromise: Promise<File[]> | null = null;

export function invalidateDraftFilesCache() {
  cachedDraftFiles = null;
  cachedDraftFilesPromise = null;
}

async function readDraftFileRecords(db: IDBDatabase, keys?: string[]) {
  return new Promise<DraftFileRecord[]>((resolve, reject) => {
    const tx = db.transaction(FILE_STORE_NAME, "readonly");
    const store = tx.objectStore(FILE_STORE_NAME);

    if (!keys) {
      const request = store.getAll();
      request.onerror = () => {
        reject(request.error ?? new Error("Failed to load draft files"));
      };
      request.onsuccess = () => {
        resolve(request.result);
      };
      return;
    }

    if (keys.length === 0) {
      resolve([]);
      return;
    }

    const records: DraftFileRecord[] = [];
    let pending = keys.length;

    for (const key of keys) {
      const request = store.get(key);
      request.onerror = () => {
        reject(request.error ?? new Error("Failed to load draft file"));
      };
      request.onsuccess = () => {
        const record = request.result as DraftFileRecord | undefined;
        if (record) records.push(record);
        pending -= 1;
        if (pending === 0) resolve(records);
      };
    }
  });
}

async function recordsToFiles(records: DraftFileRecord[]) {
  const files = await Promise.all(records.map(recordToFile));
  return files.filter((file): file is File => file !== null);
}

function thumbnailFileKey(storageKey: string) {
  return storageKey.replace(/:v\d+$/, "");
}

async function pruneDraftThumbnailsExcept(keepKeys: Set<string>) {
  const keys = await listDraftThumbnailKeys();

  await Promise.all(
    keys
      .filter((storageKey) => !keepKeys.has(thumbnailFileKey(storageKey)))
      .map((storageKey) => deleteDraftThumbnail(storageKey)),
  );
}

/**
 * Upserts records for `files` and deletes any record whose key is not in
 * `keepKeys`. Callers include claimed keys from other sessions in `keepKeys`
 * so saving one selection never evicts another upload's source files.
 */
async function syncDraftFileRecords(
  db: IDBDatabase,
  files: File[],
  keepKeys: Set<string>,
) {
  const existing = await readDraftFileRecords(db);

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(FILE_STORE_NAME, "readwrite");
    const store = tx.objectStore(FILE_STORE_NAME);

    for (const record of existing) {
      if (!keepKeys.has(record.key)) {
        store.delete(record.key);
      }
    }

    for (const file of files) {
      const key = getFileKey(file);
      store.put({
        key,
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
        storage: "opfs",
      });
    }

    tx.oncomplete = () => resolve();
    tx.onerror = () => {
      reject(tx.error ?? new Error("Failed to save draft files"));
    };
  });
}

async function deleteDraftFileRecords(db: IDBDatabase, keys: string[]) {
  if (keys.length === 0) return;

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(FILE_STORE_NAME, "readwrite");
    const store = tx.objectStore(FILE_STORE_NAME);
    for (const key of keys) {
      store.delete(key);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => {
      reject(tx.error ?? new Error("Failed to delete draft files"));
    };
  });
}

export async function listDraftFileDescriptors(keys?: string[]) {
  const db = await openSurfClipsAIDb();

  try {
    const records = await readDraftFileRecords(db, keys);
    return records.map(({ key, name, type, lastModified }) => ({
      key,
      name,
      type,
      lastModified,
    }));
  } finally {
    db.close();
  }
}

export async function hydrateDraftFile(descriptor: DraftFileDescriptor) {
  return recordToFile({ ...descriptor, storage: "opfs" });
}

export async function hydrateDraftFiles(descriptors: DraftFileDescriptor[]) {
  const files = await Promise.all(descriptors.map(hydrateDraftFile));
  return files.filter((file): file is File => file !== null);
}

export async function loadDraftFilesByKeys(keys: string[]) {
  const db = await openSurfClipsAIDb();

  try {
    const records = await readDraftFileRecords(db, keys);
    return recordsToFiles(records);
  } finally {
    db.close();
  }
}

export async function loadDraftFiles() {
  if (cachedDraftFiles) return cachedDraftFiles;
  if (cachedDraftFilesPromise) return cachedDraftFilesPromise;

  cachedDraftFilesPromise = (async () => {
    const db = await openSurfClipsAIDb();

    try {
      const records = await readDraftFileRecords(db);
      if (records.length === 0) {
        logDraft("No saved draft clips found");
        return [];
      }

      const files = await recordsToFiles(records);
      if (files.length === 0) {
        console.error(
          "Draft file metadata exists but clip files could not be restored from disk",
        );
        return [];
      }

      logDraft(`Restored ${describeDraftFiles(files)} from local storage`);
      cachedDraftFiles = files;
      return files;
    } finally {
      db.close();
    }
  })().finally(() => {
    cachedDraftFilesPromise = null;
  });

  return cachedDraftFilesPromise;
}

export async function saveDraftFiles(
  files: File[],
  options: SaveDraftFilesOptions = {},
) {
  if (files.length === 0 && !options.allowEmpty) return;

  if (!canStoreDraftFilesInOpfs()) {
    throw new DraftStorageUnavailableError();
  }

  const generation = draftStorageGeneration;

  // Preserve every file an in-flight upload still needs, so saving one
  // selection never deletes another session's source blobs.
  const claimedKeys = await collectClaimedFileKeys();
  assertDraftStorageActive(generation);

  const db = await openSurfClipsAIDb();

  try {
    if (files.length === 0) {
      logDraft("Clearing unclaimed draft clips");
      await removeDraftFilesFromOpfsExcept(claimedKeys);
      assertDraftStorageActive(generation);
      const existing = await readDraftFileRecords(db);
      await deleteDraftFileRecords(
        db,
        existing
          .map((record) => record.key)
          .filter((key) => !claimedKeys.has(key)),
      );
      await pruneDraftThumbnailsExcept(claimedKeys);
      invalidateDraftFilesCache();
      logDraft("Draft cleared");
      return;
    }

    const keepKeys = new Set<string>(claimedKeys);
    for (const file of files) keepKeys.add(getFileKey(file));

    const existing = await readDraftFileRecords(db);
    assertDraftStorageActive(generation);
    const existingKeys = new Set(existing.map((record) => record.key));

    logDraft(`Saving ${describeDraftFiles(files)} to local storage…`);

    for (const [index, file] of files.entries()) {
      assertDraftStorageActive(generation);

      const key = getFileKey(file);
      if (existingKeys.has(key)) {
        logDraft(`Skipping unchanged ${index + 1}/${files.length}: ${file.name}`);
        options.onFileProgress?.(key, file.size, file.size);
        continue;
      }

      logDraft(
        `Writing ${index + 1}/${files.length}: ${file.name} (${formatDraftByteSize(file.size)})`,
      );
      await writeDraftFileToOpfs(key, file, (bytesWritten) => {
        options.onFileProgress?.(key, bytesWritten, file.size);
      });
    }

    assertDraftStorageActive(generation);
    await syncDraftFileRecords(db, files, keepKeys);

    assertDraftStorageActive(generation);
    await removeDraftFilesFromOpfsExcept(keepKeys);
    await pruneDraftThumbnailsExcept(keepKeys);
    invalidateDraftFilesCache();
    logDraft(
      `Saved ${describeDraftFiles(files)}. Safe to refresh.`,
    );
  } catch (error) {
    if (error instanceof DraftStorageCancelledError) {
      logDraft("Save cancelled");
      return;
    }
    throw error;
  } finally {
    db.close();
  }
}

/**
 * Clears the picker's unsubmitted selection while leaving intact every file
 * claimed by an in-flight upload session, so simultaneous uploads keep their
 * resumable source blobs.
 */
export async function clearDraftSelection() {
  // Cancel any in-flight picker save so a stale debounced write cannot
  // re-add the cleared selection after this completes.
  bumpDraftStorageGeneration();
  await saveDraftFiles([], { allowEmpty: true });
}

/**
 * Releases the source blobs of a finished upload session. Keys still claimed by
 * another in-flight session are kept so concurrent uploads remain resumable.
 * The manifest and session record are intentionally left untouched (they act
 * as the completion ledger).
 */
export async function releaseSessionFiles(clientSessionId: string) {
  const manifest = await loadUploadManifest(clientSessionId);
  if (!manifest) return;

  const claimedByOthers = await collectClaimedFileKeys(clientSessionId);
  const releasableKeys = manifest.files
    .map((file) => file.clientFileKey)
    .filter((key) => !claimedByOthers.has(key));

  if (releasableKeys.length === 0) return;

  invalidateDraftFilesCache();

  await Promise.all(releasableKeys.map((key) => removeDraftFileFromOpfs(key)));

  const db = await openSurfClipsAIDb();
  try {
    await deleteDraftFileRecords(db, releasableKeys);
  } finally {
    db.close();
  }

  const releasableKeySet = new Set(releasableKeys);
  const thumbnailKeys = await listDraftThumbnailKeys();
  await Promise.all(
    thumbnailKeys
      .filter((storageKey) => releasableKeySet.has(thumbnailFileKey(storageKey)))
      .map((storageKey) => deleteDraftThumbnail(storageKey)),
  );

  logDraft(
    `Released ${releasableKeys.length} clip blob(s) for completed session ${clientSessionId}`,
  );
}

export async function listDraftThumbnailKeys() {
  const db = await openSurfClipsAIDb();

  try {
    return await new Promise<string[]>((resolve, reject) => {
      const tx = db.transaction(THUMBNAIL_STORE_NAME, "readonly");
      const request = tx.objectStore(THUMBNAIL_STORE_NAME).getAllKeys();

      request.onerror = () => {
        reject(request.error ?? new Error("Failed to list draft thumbnails"));
      };

      request.onsuccess = () => {
        resolve((request.result as string[]) ?? []);
      };
    });
  } finally {
    db.close();
  }
}

export async function loadDraftThumbnail(key: string) {
  const db = await openSurfClipsAIDb();

  try {
    const record = await new Promise<ThumbnailRecord | undefined>(
      (resolve, reject) => {
        const tx = db.transaction(THUMBNAIL_STORE_NAME, "readonly");
        const request = tx.objectStore(THUMBNAIL_STORE_NAME).get(key);

        request.onerror = () => {
          reject(request.error ?? new Error("Failed to load draft thumbnail"));
        };

        request.onsuccess = () => {
          resolve(request.result);
        };
      },
    );

    return record?.blob ?? null;
  } finally {
    db.close();
  }
}

export async function saveDraftThumbnail(key: string, blob: Blob) {
  const generation = draftStorageGeneration;
  const db = await openSurfClipsAIDb();

  try {
    assertDraftStorageActive(generation);

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(THUMBNAIL_STORE_NAME, "readwrite");
      tx.objectStore(THUMBNAIL_STORE_NAME).put({ key, blob });

      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        reject(tx.error ?? new Error("Failed to save draft thumbnail"));
      };
    });
  } catch (error) {
    if (error instanceof DraftStorageCancelledError) return;
    throw error;
  } finally {
    db.close();
  }
}

export async function deleteDraftThumbnail(key: string) {
  const db = await openSurfClipsAIDb();

  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(THUMBNAIL_STORE_NAME, "readwrite");
      tx.objectStore(THUMBNAIL_STORE_NAME).delete(key);

      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        reject(tx.error ?? new Error("Failed to delete draft thumbnail"));
      };
    });
  } finally {
    db.close();
  }
}
